import React, { useState } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import css from './PaintingVariantSelector.module.css';

/**
 * Component for selecting painting variants (size, material, price, quantity)
 * Displays variants in a card-based layout with selection functionality
 */
const PaintingVariantSelector = props => {
  const {
    variants = [],
    currentUser,
    onVariantSelect,
    selectedVariant,
    className,
    rootClassName,
  } = props;

  const [activeVariantIndex, setActiveVariantIndex] = useState(
    selectedVariant ? variants.findIndex(v => v === selectedVariant) : 0
  );

  if (!variants || variants.length === 0) {
    return null;
  }

  const handleVariantClick = (variant, index) => {
    setActiveVariantIndex(index);
    if (onVariantSelect) {
      onVariantSelect(variant);
    }
  };

  // Helper function to format quantity display
  const formatQuantity = quantity => {
    const qty = parseInt(quantity);
    return qty > 100 ? '100+' : qty.toString();
  };

  const classes = classNames(rootClassName || css.root, className);
  const getUserMembershipTier = currentUser => {
    const membership = currentUser?.attributes?.profile?.publicData?.membership;
    const membershipTier = membership?.membershipTier;
    const membershipActive = membership?.membershipActive;
    const expiryDate = membership?.membershipExpiryDate;

    // Check if membership is active and not expired
    const isActive = membershipActive === true;
    const isExpired = expiryDate ? new Date(expiryDate) < new Date() : false;

    if (!isActive || isExpired) {
      return null; // No active membership
    }

    return membershipTier; // 'seed', 'thrive', or null
  };
  const currentTier = getUserMembershipTier(currentUser);

  return (
    <div className={classes}>
      {currentTier ? (
        <div className={css.currentTierContainer}>
          <span className={css.currentTierLabel}>Your current tier is </span>
          <span
            className={classNames(css.currentTierValue, {
              [css.tierSeed]: currentTier === 'seed',
              [css.tierThrive]: currentTier === 'thrive',
              [css.tierFree]: currentTier === 'free',
            })}
          >
            {currentTier.charAt(0).toUpperCase() + currentTier.slice(1)}
          </span>
        </div>
      ) : (
        <div className={css.currentTierContainer}>
          <span className={css.currentTierLabel}>Your current tier is </span>

          <span
            className={classNames(css.currentTierValue, {
              [css.tierFree]: currentTier !== 'free',
            })}
          >
            Free
          </span>
        </div>
      )}

      <h3 className={css.title}>
        <FormattedMessage id="PaintingVariantSelector.title" />
      </h3>
      <p className={css.description}>
        <FormattedMessage id="PaintingVariantSelector.description" />
      </p>

      <div className={css.variantsContainer}>
        {variants.map((variant, index) => {
          const isActive = index === activeVariantIndex;
          const isOutOfStock = parseInt(variant.quantity) === 0;

          const variantClasses = classNames(css.variantCard, {
            [css.variantCardActive]: isActive,
            [css.variantCardOutOfStock]: isOutOfStock,
          });

          return (
            <button
              key={index}
              type="button"
              className={variantClasses}
              onClick={() => !isOutOfStock && handleVariantClick(variant, index)}
              disabled={isOutOfStock}
            >
              {isActive && !isOutOfStock && (
                <div className={css.selectedIndicator}>
                  <svg
                    className={css.checkIcon}
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 6L9 17L4 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}

              {isOutOfStock && (
                <span className={css.outOfStockBadge}>
                  <FormattedMessage id="PaintingVariantSelector.outOfStock" />
                </span>
              )}

              <div className={css.variantHeader}>
                <span className={css.variantMaterial}>{variant.material}</span>
              </div>

              <div className={css.variantDetails}>
                {variant.size && (
                  <div className={css.variantSize}>
                    <span className={css.label}>
                      <FormattedMessage id="PaintingVariantSelector.size" />:{' '}
                    </span>
                    <span className={css.value}>{variant.size}</span>
                  </div>
                )}

                <div className={css.variantPrice}>
                  <span className={css.priceValue}>£{parseFloat(variant.price).toFixed(2)}</span>
                </div>

                <div className={css.variantQuantity}>
                  <span className={css.label}>
                    <FormattedMessage id="PaintingVariantSelector.available" />:{' '}
                  </span>
                  <span className={css.value}>{formatQuantity(variant.quantity)}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PaintingVariantSelector;
